import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useToast } from '../../hooks/useToast'

type Step = 'welcome' | 'company' | 'integration' | 'done'
type Method = 'tiny' | 'spreadsheet' | 'skip' | null

interface Props {
  companyId: string
  onComplete: () => Promise<void>
}

export default function OnboardingWizard({ companyId, onComplete }: Props) {
  const [step, setStep] = useState<Step>('welcome')
  const [companyName, setCompanyName] = useState('')
  const [method, setMethod] = useState<Method>(null)
  const [tinyToken, setTinyToken] = useState('')
  const [saving, setSaving] = useState(false)
  const { notify } = useToast()
  const navigate = useNavigate()

  async function saveCompanyName() {
    if (!companyName.trim()) {
      notify('error', 'Informe o nome da empresa.')
      return
    }
    setSaving(true)
    const { error } = await supabase.from('companies').update({ name: companyName.trim() }).eq('id', companyId)
    setSaving(false)
    if (error) { notify('error', error.message); return }
    setStep('integration')
  }

  async function finish() {
    setSaving(true)
    // Persiste a escolha de provider ERP (foundation do adapter pattern)
    const erpProvider = method === 'tiny' ? 'tiny' : method === 'spreadsheet' ? 'file' : 'none'
    const updates: Record<string, unknown> = { erp_provider: erpProvider }
    if (method === 'tiny' && tinyToken.trim()) {
      updates.tiny_api_token = tinyToken.trim()
    }
    const { error } = await supabase.from('companies').update(updates).eq('id', companyId)
    if (error) { notify('error', error.message); setSaving(false); return }
    await onComplete()
    setSaving(false)

    if (method === 'spreadsheet') navigate('/configuracoes')
    else if (method === 'tiny') navigate('/configuracoes')
    else navigate('/')
  }

  return (
    <div className="fixed inset-0 z-50 bg-paper/95 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-xl bg-surface border border-line rounded-2xl p-8 shadow-2xl">

        {/* Indicador de progresso */}
        <div className="flex items-center gap-2 mb-8">
          {(['welcome', 'company', 'integration'] as Step[]).map((s, i) => {
            const order: Step[] = ['welcome', 'company', 'integration']
            const currentIdx = order.indexOf(step)
            const active = i <= currentIdx
            return (
              <div key={s} className="flex-1 flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                  active ? 'bg-brand-600 text-white' : 'bg-sunken text-ink-faint'
                }`}>
                  {i + 1}
                </div>
                {i < 2 && <div className={`flex-1 h-0.5 ${active && i < currentIdx ? 'bg-brand-600' : 'bg-line'}`} />}
              </div>
            )
          })}
        </div>

        {/* Etapa 1: boas-vindas */}
        {step === 'welcome' && (
          <div>
            <img src="/logo-ax.svg" alt="" className="h-16 w-auto mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-ink text-center mb-2">Bem-vindo ao ax-kanban! 👋</h2>
            <p className="text-ink-muted text-center mb-8">
              Vamos configurar sua conta em 2 passos rápidos.
            </p>
            <button
              onClick={() => setStep('company')}
              className="w-full bg-brand-600 hover:bg-brand-700 text-white font-semibold py-3 rounded-lg transition-colors"
            >
              Começar
            </button>
          </div>
        )}

        {/* Etapa 2: nome da empresa */}
        {step === 'company' && (
          <div>
            <h2 className="text-xl font-bold text-ink mb-2">Como sua empresa se chama?</h2>
            <p className="text-ink-muted text-sm mb-6">
              Esse nome aparece nos pedidos de compra que você emitir e nos PDFs enviados aos fornecedores.
            </p>
            <input
              type="text"
              value={companyName}
              onChange={e => setCompanyName(e.target.value)}
              placeholder="Ex: Mercado Saudável Ltda"
              autoFocus
              className="w-full bg-paper border border-line rounded-lg px-4 py-3 text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand-500 mb-6"
            />
            <div className="flex gap-2">
              <button
                onClick={() => setStep('welcome')}
                className="px-4 py-2.5 bg-sunken hover:bg-line text-ink-muted rounded-lg text-sm"
              >
                Voltar
              </button>
              <button
                onClick={saveCompanyName}
                disabled={saving}
                className="flex-1 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg text-sm transition-colors"
              >
                {saving ? 'Salvando...' : 'Continuar'}
              </button>
            </div>
          </div>
        )}

        {/* Etapa 3: integração */}
        {step === 'integration' && (
          <div>
            <h2 className="text-xl font-bold text-ink mb-2">Como você quer importar produtos?</h2>
            <p className="text-ink-muted text-sm mb-6">
              Escolha o método que mais combina com você. Pode trocar depois nas Configurações.
            </p>

            <div className="space-y-2 mb-6">
              <button
                onClick={() => setMethod('tiny')}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  method === 'tiny' ? 'border-brand-600 bg-brand-600/10' : 'border-line hover:border-sunken bg-sunken/30'
                }`}
              >
                <div className="font-semibold text-ink">🔌 Tiny ERP</div>
                <div className="text-xs text-ink-muted mt-0.5">Sincronização automática via token de API</div>
              </button>

              <button
                onClick={() => setMethod('spreadsheet')}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  method === 'spreadsheet' ? 'border-brand-600 bg-brand-600/10' : 'border-line hover:border-sunken bg-sunken/30'
                }`}
              >
                <div className="font-semibold text-ink">📊 Excel ou CSV</div>
                <div className="text-xs text-ink-muted mt-0.5">Subir planilha com seus produtos</div>
              </button>

              <button
                onClick={() => setMethod('skip')}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  method === 'skip' ? 'border-brand-600 bg-brand-600/10' : 'border-line hover:border-sunken bg-sunken/30'
                }`}
              >
                <div className="font-semibold text-ink">⏭️ Pular por agora</div>
                <div className="text-xs text-ink-muted mt-0.5">Configuro depois nas Configurações</div>
              </button>

              <div className="text-xs text-ink-faint px-2 pt-2">
                💡 Em breve: integração com Bling, Omie, Conta Azul, TOTVS e SAP B1.
                {' '}Use Tiny ou planilha por enquanto — depois migramos sem perder dados.
              </div>
            </div>

            {method === 'tiny' && (
              <div className="mb-6">
                <label className="block text-xs font-semibold text-ink-muted uppercase mb-1.5">Token de API (opcional agora)</label>
                <input
                  type="password"
                  value={tinyToken}
                  onChange={e => setTinyToken(e.target.value)}
                  placeholder="Cole aqui ou deixe em branco para configurar depois"
                  className="w-full bg-paper border border-line rounded-lg px-4 py-2.5 text-ink placeholder:text-ink-faint focus:outline-none focus:border-brand-500"
                />
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => setStep('company')}
                className="px-4 py-2.5 bg-sunken hover:bg-line text-ink-muted rounded-lg text-sm"
              >
                Voltar
              </button>
              <button
                onClick={finish}
                disabled={!method || saving}
                className="flex-1 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2.5 rounded-lg text-sm transition-colors"
              >
                {saving ? 'Concluindo...' : 'Concluir'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
